import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { sendContactForm, UserInfo } from '../Utility/sharedFunctions';
import '../CSS/HomePage.css';
import { HOME_SERVICES } from '../Data/homeServices';
import { HOME_FAQ } from '../Data/faqs';
import { isPrerender } from '../Utility/prerender';
import FaqSection from './FaqSection';

const HomePage: React.FC = () => {
  const [activeService, setActiveService] = useState('autonomous');
  const [activeTab, setActiveTab] = useState('workflow');
  const [formData, setFormData] = useState<UserInfo>({
    name: '',
    email: '',
    phoneNumber: '',
    inquiry: '',
    message: ''
  });
  const whyRef = useRef<HTMLDivElement>(null);
  const [whyArmed, setWhyArmed] = useState(false);
  const [whyVisible, setWhyVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Pre-rendered markup has no `selected` attribute on the placeholder option, so sync the selects once hydrated.
  useEffect(() => {
    document.querySelectorAll<HTMLSelectElement>('select[name="inquiry"]').forEach((select) => {
      select.value = '';
    });
  }, []);

  // Reveal the "Why Choose" section when it scrolls into view (content stays visible without JavaScript)
  useEffect(() => {
    const el = whyRef.current;
    if (!el || typeof IntersectionObserver === 'undefined' || isPrerender()) return;
    setWhyArmed(true);
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setWhyVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const activeServiceData = HOME_SERVICES.find((service) => service.id === activeService) || HOME_SERVICES[0];
  const activeTabData = activeServiceData.tabs.find((tab) => tab.id === activeTab) || activeServiceData.tabs[0];

  // Reset tab when service changes
  const handleServiceChange = (service: string) => {
    setActiveService(service);
    // Reset to first tab of the new service
    if (service === 'enterprise') {
      setActiveTab('strategy');
    } else if (service === 'orchestration') {
      setActiveTab('orchestrators');
    } else {
      setActiveTab('workflow');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      await sendContactForm(formData);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phoneNumber: '',
        inquiry: '',
        message: ''
      });
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to send message');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToServices = () => {
    const servicesSection = document.querySelector('.homepage_services');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToWhyChoose = () => {
    const whyChooseSection = document.querySelector('.why_zemantics');
    if (whyChooseSection) {
      whyChooseSection.scrollIntoView({ behavior: 'smooth' });
    }
  };
  return (
    <div className="home-page">
      {/* Hero Banner with Video Background */}
      <div className="home_banner">
        <video
          className="banner-video"
          autoPlay
          muted
          loop
          playsInline
        >
          <source src="/Assets/Home Page/2127-155244168.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="homepage-banner-overlay"></div>
        <div className="home_banner-content">
          <h1 className="home_banner-title">
            Infuse intelligence into your operations with <span className="highlight">Advanced AI</span>
          </h1>
          <div className="banner-buttons">
            <button className="home_banner-btn primary" onClick={scrollToServices}>Get Started</button>
            <button className="home_banner-btn secondary" onClick={scrollToWhyChoose}>Learn More</button>
          </div>
        </div>
      </div>

      {/* Zemantics Mantra Section */}
      <div className="zemantics_mantra">
        <p>Zemantics designs and deploys intelligent agents, agentic orchestrators and data decision pipelines that take on the work behind your operations, turning your data into decisions and those decisions into action.</p>
      </div>

      {/* Homepage Services Section */}
      <div className="homepage_services">
        <h2 className="services-title">Our Services</h2>
        <div className="services-container">
          {/* Left Side - Service Categories */}
          <div className="services-left">
            {HOME_SERVICES.map((service, index) => (
              <div
                key={service.id}
                className={`service-category ${activeService === service.id ? 'active' : ''}`}
                onClick={() => handleServiceChange(service.id)}
              >
                <span className="service-number">{`0${index + 1}`}</span>
                <span className="service-name">{service.name}</span>
              </div>
            ))}
          </div>

          {/* Right Side - Service Details */}
          <div className="services-right">
            {/* Service Tabs */}
            <div className="service-tabs">
              {activeServiceData.tabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`service-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Service Content */}
            <div className="service-content">
              {activeTabData && (
                <div>
                  {activeTabData.paragraphs.map((text) => (
                    <p key={text}>{text}</p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* AI Statistics Section */}
      <div className="ai_statistics">
        <div className="ai_statistics-content">
          <h2 className="ai_statistics-title">Put AI Agents to Work Across Your Operations</h2>
          <div className="ai_statistics-grid">
            <div className="stat-item">
              <h3 className="stat-title">Customer Engagements</h3>
              <div className="stat-value">100+</div>
              <p className="stat-description">AND GROWING</p>
            </div>
            <div className="stat-item">
              <h3 className="stat-title">Agents Deployed</h3>
              <div className="stat-value">270+</div>
              <p className="stat-description">AND THRIVING</p>
            </div>
            <div className="stat-item">
              <h3 className="stat-title">Total ARR Saved</h3>
              <div className="stat-value">$135+ M</div>
              <p className="stat-description">AND MULTIPLYING</p>
            </div>
          </div>
        </div>
      </div>

      {/* Why Choose Zemantics Section */}
      <div
        ref={whyRef}
        className={`why_zemantics${whyArmed ? ' armed' : ''}${whyVisible ? ' in-view' : ''}`}
      >
        <div className="why_zemantics-content">
          <h2 className="why_zemantics-title">
            Why Choose <span className="zemantics-gradient">Zemantics</span>
          </h2>
          <p className="why-subtitle">Agentic AI and data science, built on evidence your teams can trust.</p>

          <div className="why-flow" aria-hidden="true">
            <span className="why-node">Data</span>
            <span className="why-line"></span>
            <span className="why-node">Decisions</span>
            <span className="why-line"></span>
            <span className="why-node">Action</span>
          </div>

          <div className="why-tiles">
            <div className="why-tile">
              <span className="why-tile-number">01</span>
              <h3 className="why-tile-title">Attitude</h3>
              <p className="why-tile-text">Evidence-based decisions, always.</p>
            </div>
            <div className="why-tile">
              <span className="why-tile-number">02</span>
              <h3 className="why-tile-title">Mission</h3>
              <p className="why-tile-text">Turn data into decisions, and decisions into action.</p>
            </div>
            <div className="why-tile">
              <span className="why-tile-number">03</span>
              <h3 className="why-tile-title">Experience</h3>
              <p className="why-tile-text">Mission-critical projects across Finance, Telecom, Healthcare, Pharma, Insurance &amp; FMCG.</p>
            </div>
            <div className="why-tile">
              <span className="why-tile-number">04</span>
              <h3 className="why-tile-title">Innovation</h3>
              <p className="why-tile-text">An innovation lab where every idea is explored, from new models to working POCs.</p>
            </div>
          </div>

          <p className="why-more">
            <Link to="/products/" className="why-link">Explore the Zemantics Catalyst Suite</Link>
          </p>
        </div>
      </div>

      <FaqSection items={HOME_FAQ} tint />

             {/* Contact Us Section */}
       <div 
         id="contact-section"
         className="contact_us"
         style={{
           backgroundImage: `url('${process.env.PUBLIC_URL}/Assets/Home Page/reach-out-today-section-image.png')`
         }}
       >
        <div className="contact_us-content">
          {/* Left Side - Text Content */}
          <div className="contact_us-text">
            <h2 className="contact_us-title">Reach Out to Us Today!</h2>
            <p className="contact_us-desc">Our team is ready to help you move forward with the right solution. Let us know a bit about you, and we'll connect shortly!</p>
          </div>

                     {/* Right Side - Contact Form */}
           <div className="contact_us-form desktop-contact-form">
                           {submitStatus === 'success' && (
                <div className="homepage-success-message">
                  Thank you! Your message has been sent successfully. We'll get back to you soon.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="homepage-error-message">
                  {errorMessage}
                </div>
              )}

             <form className="contact-form" onSubmit={handleSubmit}>
               <div className="form-row">
                 <input 
                   type="text" 
                   name="name"
                   aria-label="Your name"
                   placeholder="Your Name" 
                   className="form-input"
                   value={formData.name}
                   onChange={handleInputChange}
                   required
                 />
                 <input 
                   type="email" 
                   name="email"
                   aria-label="Email"
                   placeholder="Email" 
                   className="form-input"
                   value={formData.email}
                   onChange={handleInputChange}
                   required
                 />
               </div>
               <div className="form-row">
                 <input 
                   type="tel" 
                   name="phoneNumber"
                   aria-label="Phone"
                   placeholder="Phone" 
                   className="form-input"
                   value={formData.phoneNumber}
                   onChange={handleInputChange}
                   required
                 />
                 <select 
                   name="inquiry"
                   aria-label="Inquiry"
                   className="form-select"
                   value={formData.inquiry}
                   onChange={handleInputChange}
                   required
                 >
                  <option value="" disabled>Inquiry</option>
                  <option value="Artificial Intelligence And ML">Artificial Intelligence And ML</option>
                  <option value="Data Engineering">Data Engineering</option>
                  <option value="Generative AI">Generative AI</option>
                  <option value="Software Engineering For AI Products">Software Engineering For AI Products</option>
                  <option value="Catalyst Suite">Catalyst Suite</option>
                  <option value="General Inquiry">General Inquiry</option>
                 </select>
               </div>
               <textarea 
                 name="message"
                 aria-label="Message"
                 placeholder="Message" 
                 className="form-textarea"
                 rows={5}
                 value={formData.message}
                 onChange={handleInputChange}
                 required
               ></textarea>
               <button 
                 type="submit" 
                 className="form-submit"
                 disabled={isSubmitting}
               >
                 {isSubmitting ? 'Sending...' : 'Make a Request'}
               </button>
             </form>
           </div>

           {/* Mobile Contact Form */}
           <div className="contact_us-form mobile-contact-form">
             {submitStatus === 'success' && (
               <div className="homepage-success-message">
                 Thank you! Your message has been sent successfully. We'll get back to you soon.
               </div>
             )}

             {submitStatus === 'error' && (
               <div className="homepage-error-message">
                 {errorMessage}
               </div>
             )}

             <form className="contact-form homepage-mobile-contact-form-inner" onSubmit={handleSubmit}>
               <input 
                 type="text" 
                 name="name"
                 aria-label="Your name"
                 placeholder="Your Name" 
                 className="form-input homepage-mobile-form-input"
                 value={formData.name}
                 onChange={handleInputChange}
                 required
               />
               <input 
                 type="email" 
                 name="email"
                 aria-label="Email"
                 placeholder="Email" 
                 className="form-input homepage-mobile-form-input"
                 value={formData.email}
                 onChange={handleInputChange}
                 required
               />
               <input 
                 type="tel" 
                 name="phoneNumber"
                 aria-label="Phone"
                 placeholder="Phone" 
                 className="form-input homepage-mobile-form-input"
                 value={formData.phoneNumber}
                 onChange={handleInputChange}
                 required
               />
               <select 
                 name="inquiry"
                   aria-label="Inquiry"
                 className="form-select homepage-mobile-form-select"
                 value={formData.inquiry}
                 onChange={handleInputChange}
                 required
               >
                <option value="" disabled>Inquiry</option>
                <option value="Artificial Intelligence And ML">Artificial Intelligence And ML</option>
                <option value="Data Engineering">Data Engineering</option>
                <option value="Generative AI">Generative AI</option>
                <option value="Software Engineering For AI Products">Software Engineering For AI Products</option>
                  <option value="Catalyst Suite">Catalyst Suite</option>
                <option value="General Inquiry">General Inquiry</option>
               </select>
               <textarea 
                 name="message"
                 aria-label="Message"
                 placeholder="Message" 
                 className="form-textarea homepage-mobile-form-textarea"
                 rows={5}
                 value={formData.message}
                 onChange={handleInputChange}
                 required
               ></textarea>
               <button 
                 type="submit" 
                 className="form-submit homepage-mobile-form-submit"
                 disabled={isSubmitting}
               >
                 {isSubmitting ? 'Sending...' : 'Make a Request'}
               </button>
             </form>
           </div>
        </div>
      </div>

    </div>
  );
};

export default HomePage; 